import { Button, IconButton } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';

export interface DeletePersonModalProps {
  readonly personName: string | null;
  readonly open: boolean;
  readonly isLoading?: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
}

/**
 * Modal de confirmação para excluir uma pessoa.
 *
 * @param personName - Nome da pessoa a ser excluída, ou `null` quando o modal está fechado
 * @param open - Controla visibilidade do modal
 * @param isLoading - Exibe spinner no botão de confirmação
 * @param onClose - Callback ao fechar sem confirmar
 * @param onConfirm - Callback ao confirmar a exclusão
 */
export function DeletePersonModal({
  personName,
  open,
  isLoading = false,
  onClose,
  onConfirm,
}: DeletePersonModalProps) {
  const { t } = useTranslation('personManagement');

  if (!personName) return null;

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-mini">
            <Dialog.Title className="ts-heading-sm text-ctx-content-title">
              {t('deletePersonModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('deletePersonModal.cancel')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="px-xs py-mini">
            <p className="ts-paragraph-xs text-ctx-content-base">
              {t('deletePersonModal.message', { name: personName })}
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-xs py-mini">
            <Button hierarchy="secondary" size="lg" onClick={onClose} disabled={isLoading}>
              {t('deletePersonModal.cancel')}
            </Button>
            <Button hierarchy="danger" size="lg" onClick={onConfirm} isLoading={isLoading}>
              {t('deletePersonModal.confirm')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
